import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';

import type { ClauseDetailResponse, ClauseVersion, ManageClauseRequest, ManageClauseResponse } from '@clm/shared-types';

import { fetchClauseDetail, manageClause } from '../api/clauses';
import { ApiError } from '../api/errors';
import { classifyApiError, type ApiProblemKind } from '../api/problem';
import { ErrorNotice } from '../components/ErrorNotice';
import { ProvenanceChip } from '../components/ProvenanceChip';
import { requireSurface } from '../routes/surfaces';

/**
 * Clause detail — FR-16, FR-17, NFR-13, CR-01 · CLM-55. `/clauses/:clause_code`.
 *
 * One clause: its current master text, its whole version chain (0021 keeps one
 * row per version; 0083 adds the reason), each version's embedding state, and
 * the two writes FR-17 reserves to Legal — **Edit master text** (a new version,
 * never an in-place change) and **Retire / Restore** (a toggle, no new version).
 *
 * ## The edit names its cost
 *
 * A revision sends `expected_version_no` — the version this form was opened on —
 * so a stale screen is a 409 and never a silent overwrite. The success message
 * repeats what the API reported: how many recorded judgements the edit made
 * stale, and how many contracts *you can see* still reference the old text.
 *
 * ## Errors are distinct, and a conflict is not an error
 *
 * `conflict` renders in the neutral tone with a Reload action: someone else
 * revised the clause since it was opened, nothing failed. It is rendered by this
 * surface rather than `ErrorNotice`, whose conflict copy is verify-field's
 * ("Another reviewer settled this field") — generalising that copy is G12, a
 * follow-up after deploy-api, not this lane.
 *
 * `hasRole` dims, never hides: a `contributor` sees the buttons and the API
 * answers 403 (B-07 in production, where nobody holds `legal_reviewer` yet).
 */

type Phase =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly response: ClauseDetailResponse }
  | { readonly kind: 'problem'; readonly problem: Problem };

interface Problem {
  readonly kind: ApiProblemKind;
  readonly detail: string | null;
  readonly correlation_id: string | null;
}

function toProblem(error: unknown): Problem {
  return {
    kind: classifyApiError(error),
    detail: error instanceof ApiError ? error.message : null,
    correlation_id: error instanceof ApiError ? error.correlation_id : null,
  };
}

type WritePhase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'busy' }
  | { readonly kind: 'problem'; readonly problem: Problem }
  | { readonly kind: 'saved'; readonly response: ManageClauseResponse };

export function ClauseDetail() {
  const { clause_code: clauseCode } = useParams<{ clause_code: string }>();
  const surface = requireSurface('clauses');

  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [reloadNonce, setReloadNonce] = useState(0);
  const [write, setWrite] = useState<WritePhase>({ kind: 'idle' });
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const editButtonRef = useRef<HTMLButtonElement | null>(null);
  const retireButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (clauseCode === undefined) return;
    const controller = new AbortController();
    void (async () => {
      try {
        const response = await fetchClauseDetail(clauseCode, controller.signal);
        if (controller.signal.aborted) return;
        setPhase({ kind: 'ready', response });
      } catch (error) {
        if (controller.signal.aborted) return;
        setPhase({ kind: 'problem', problem: toProblem(error) });
      }
    })();
    return () => {
      controller.abort();
    };
  }, [clauseCode, reloadNonce]);

  const reload = () => {
    setWrite({ kind: 'idle' });
    setPhase({ kind: 'loading' });
    setReloadNonce((n) => n + 1);
  };

  const send = async (request: ManageClauseRequest, onDone: () => void) => {
    setWrite({ kind: 'busy' });
    try {
      const response = await manageClause(request);
      setWrite({ kind: 'saved', response });
      onDone();
      // Re-fetch rather than patch rows: the version chain and the usage count
      // are the server's to state.
      setPhase({ kind: 'loading' });
      setReloadNonce((n) => n + 1);
    } catch (error) {
      setWrite({ kind: 'problem', problem: toProblem(error) });
    }
  };

  if (clauseCode === undefined) {
    return (
      <>
        <div className="hd">
          <div>
            <h2>{surface.label}</h2>
          </div>
        </div>
        <p>
          No clause code in the address. <Link to={surface.path}>Back to the clause library</Link>.
        </p>
      </>
    );
  }

  return (
    <>
      <div className="hd">
        <div>
          <p className="mut">
            <Link to={surface.path}>{surface.label}</Link>
          </p>
          {phase.kind === 'ready' ? (
            <h2 className="cd-hd">
              <code>{phase.response.clause.clause_code}</code> · v{phase.response.clause.version_no}{' '}
              {phase.response.clause.is_active ? <span className="tag acc">Active</span> : <span className="tag out">Retired</span>}
            </h2>
          ) : (
            <h2>
              <code>{clauseCode}</code>
            </h2>
          )}
          {phase.kind === 'ready' ? (
            <p>
              {phase.response.clause.title} · Used in {phase.response.in_scope_usage_count} contracts you can see
            </p>
          ) : null}
        </div>
      </div>

      {phase.kind === 'loading' ? (
        <p role="status" aria-busy="true">
          Reading clause {clauseCode}…
        </p>
      ) : null}

      {phase.kind === 'problem' ? <DetailProblem problem={phase.problem} /> : null}

      {phase.kind === 'ready' ? (
        <>
          <section className="card" aria-labelledby="cd-text">
            <h3 id="cd-text">Master text</h3>
            <pre className="mtext">{phase.response.clause.master_text}</pre>
            <h4>Purpose text</h4>
            {phase.response.clause.purpose_text.value === null ? (
              <p className="mut">No purpose text yet — the describe step has not run.</p>
            ) : (
              <p>
                {phase.response.clause.purpose_text.value}{' '}
                <ProvenanceChip provenance={phase.response.clause.purpose_text.provenance} />
              </p>
            )}
          </section>

          <section className="card" aria-labelledby="cd-versions">
            <h3 id="cd-versions">Version history</h3>
            <VersionsTable clauseCode={phase.response.clause.clause_code} versions={phase.response.versions} />
          </section>

          <section className="card" aria-labelledby="cd-edit">
            <h3 id="cd-edit">Edit master text</h3>
            <p>
              An edit creates a new version and marks judgements recorded against the old text as
              stale (§4.3 loop 3). The new version has no vectors until the next embed run.
            </p>

            {write.kind === 'saved' && write.response.clause ? (
              <p role="status">
                Version {write.response.clause.version_no} saved. {write.response.judgements_marked_stale} recorded judgements
                are now stale; {write.response.affected_contract_keys.length} contracts you can see reference the old text.
              </p>
            ) : null}

            {write.kind === 'problem' ? (
              <WriteProblem
                problem={write.problem}
                onReload={() => {
                  reload();
                  setEditing(true);
                }}
              />
            ) : null}

            {editing ? (
              <EditForm
                clause={phase.response}
                busy={write.kind === 'busy'}
                onCancel={() => {
                  setEditing(false);
                  editButtonRef.current?.focus();
                }}
                onSubmit={(request) => {
                  void send(request, () => {
                    setEditing(false);
                    editButtonRef.current?.focus();
                  });
                }}
              />
            ) : (
              <button
                ref={editButtonRef}
                type="button"
                className="btn"
                onClick={() => {
                  setWrite({ kind: 'idle' });
                  setEditing(true);
                }}
              >
                Edit master text
              </button>
            )}
          </section>

          <section className="card" aria-labelledby="cd-retire">
            <h3 id="cd-retire">{phase.response.clause.is_active ? 'Retire' : 'Restore'}</h3>
            <p id="cd-retire-why">
              Retired clauses stay searchable by code but are excluded from ranking. This flips the
              current version in place — no new version is written.
            </p>
            {confirming ? (
              <div className="cd-confirm" role="group" aria-labelledby="cd-retire">
                <p>
                  {phase.response.clause.is_active ? 'Retire' : 'Restore'} <code>{phase.response.clause.clause_code}</code> v
                  {phase.response.clause.version_no}?
                </p>
                <div className="cform-acts">
                  <button
                    type="button"
                    className="btn"
                    disabled={write.kind === 'busy'}
                    onClick={() => {
                      void send(
                        {
                          action: 'set_clause_active',
                          clause_code: phase.response.clause.clause_code,
                          is_active: !phase.response.clause.is_active,
                        },
                        () => {
                          setConfirming(false);
                          retireButtonRef.current?.focus();
                        },
                      );
                    }}
                  >
                    Confirm {phase.response.clause.is_active ? 'retire' : 'restore'}
                  </button>
                  <button
                    type="button"
                    className="btn sm"
                    onClick={() => {
                      setConfirming(false);
                      retireButtonRef.current?.focus();
                    }}
                  >
                    Keep as is
                  </button>
                </div>
              </div>
            ) : (
              <button
                ref={retireButtonRef}
                type="button"
                className="btn"
                aria-describedby="cd-retire-why"
                onClick={() => {
                  setWrite({ kind: 'idle' });
                  setConfirming(true);
                }}
              >
                {phase.response.clause.is_active ? 'Retire' : 'Restore'}
              </button>
            )}
          </section>
        </>
      ) : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Versions                                                                    */
/* -------------------------------------------------------------------------- */

function VersionsTable({ clauseCode, versions }: { readonly clauseCode: string; readonly versions: readonly ClauseVersion[] }) {
  const [shown, setShown] = useState<ReadonlySet<string>>(new Set());
  const toggle = (id: string) => {
    setShown((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="dt-wrap">
      <table className="dt">
        <caption className="vh">Versions of {clauseCode}</caption>
        <thead>
          <tr>
            <th scope="col">Version</th>
            <th scope="col">Created</th>
            <th scope="col">By</th>
            <th scope="col">Edited</th>
            <th scope="col">Note</th>
            <th scope="col">Embedding</th>
            <th scope="col">Current</th>
          </tr>
        </thead>
        <tbody>
          {versions.map((version) => (
            <tr key={version.clause_id}>
              <td>v{version.version_no}</td>
              <td>{version.created_utc.slice(0, 10)}</td>
              <td>{version.created_by_upn ?? <span className="mut">—</span>}</td>
              <td>{version.last_edited_utc === null ? <span className="mut">—</span> : version.last_edited_utc.slice(0, 10)}</td>
              <td>{version.revision_note ?? <span className="mut">—</span>}</td>
              <td>
                {version.embedding_state === 'embedded' ? (
                  'embedded'
                ) : (
                  <span className="mut">pending — fills on the next embed-clauses run</span>
                )}
              </td>
              <td>
                {version.is_current ? (
                  'Current'
                ) : (
                  <>
                    <button
                      type="button"
                      className="btn sm"
                      aria-expanded={shown.has(version.clause_id)}
                      onClick={() => {
                        toggle(version.clause_id);
                      }}
                    >
                      {shown.has(version.clause_id) ? 'Hide text' : 'Show text'}
                    </button>
                    {shown.has(version.clause_id) ? <pre className="cver-text">{version.master_text}</pre> : null}
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Edit form                                                                   */
/* -------------------------------------------------------------------------- */

function EditForm({
  clause,
  busy,
  onCancel,
  onSubmit,
}: {
  readonly clause: ClauseDetailResponse;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (request: ManageClauseRequest) => void;
}) {
  const current = clause.clause;
  /** The version this form was opened on — a mismatch on submit is a 409. */
  const expectedVersion = current.version_no;
  const [title, setTitle] = useState(current.title);
  const [category, setCategory] = useState(current.category ?? '');
  const [clearCategory, setClearCategory] = useState(false);
  const [masterText, setMasterText] = useState(current.master_text);
  const [note, setNote] = useState('');
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const headingId = useId();

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedTitle = title.trim();
    const trimmedCategory = category.trim();
    const request: ManageClauseRequest = {
      action: 'revise_clause_text',
      clause_code: current.clause_code,
      expected_version_no: expectedVersion,
      master_text: masterText,
      ...(trimmedTitle !== current.title ? { title: trimmedTitle } : {}),
      // Clear sends null; a change sends the value; untouched sends no key.
      ...(clearCategory
        ? { category: null }
        : trimmedCategory !== (current.category ?? '') && trimmedCategory !== ''
          ? { category: trimmedCategory }
          : {}),
      revision_note: note.trim(),
    };
    onSubmit(request);
  };

  return (
    <form className="cform" aria-labelledby={headingId} onSubmit={submit}>
      <h4 id={headingId} ref={headingRef} tabIndex={-1}>
        Revise {current.clause_code} from v{expectedVersion}
      </h4>
      <label>
        Title
        <input type="text" value={title} required maxLength={200} onChange={(e) => setTitle(e.target.value)} />
      </label>
      <label>
        Category
        <input
          type="text"
          value={category}
          maxLength={60}
          disabled={clearCategory}
          onChange={(e) => setCategory(e.target.value)}
        />
      </label>
      <label className="check">
        <input type="checkbox" checked={clearCategory} onChange={(e) => setClearCategory(e.target.checked)} />
        Clear category
      </label>
      <label>
        Master text
        <textarea value={masterText} required onChange={(e) => setMasterText(e.target.value)} />
      </label>
      <label>
        Revision note
        <input type="text" value={note} required maxLength={1000} onChange={(e) => setNote(e.target.value)} />
      </label>
      <div className="cform-acts">
        <button type="submit" className="btn" disabled={busy}>
          Save as v{expectedVersion + 1}
        </button>
        <button type="button" className="btn sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/* Problems                                                                    */
/* -------------------------------------------------------------------------- */

/** The read failed. `not_found` gets the clause-specific sentence. */
function DetailProblem({ problem }: { readonly problem: Problem }) {
  return (
    <>
      <ErrorNotice kind={problem.kind} detail={problem.detail} correlation_id={problem.correlation_id} />
      {problem.kind === 'not_found' ? <p className="mut">Not found — the code may have been mistyped.</p> : null}
      {problem.kind === 'not_implemented' ? (
        <p className="mut">
          Waiting on <code>GET /v1/clauses/{'{clause_code}'}</code>.
        </p>
      ) : null}
    </>
  );
}

/** A write failed — each kind has its own copy, and a conflict is not an alert. */
function WriteProblem({ problem, onReload }: { readonly problem: Problem; readonly onReload: () => void }) {
  if (problem.kind === 'conflict') {
    return (
      <div className="banner up" role="status">
        <p className="notice-head">
          <strong>Someone revised this clause since you opened it</strong>
        </p>
        <p>
          Nothing you did failed and nothing was written against the old version — the clause simply
          moved on. Reload to see the current version and reopen the form on it.
        </p>
        {problem.correlation_id ? (
          <p className="mut">
            Correlation id <code>{problem.correlation_id}</code>
          </p>
        ) : null}
        <button type="button" className="btn sm" onClick={onReload}>
          Reload
        </button>
      </div>
    );
  }
  return (
    <>
      <ErrorNotice kind={problem.kind} detail={problem.detail} correlation_id={problem.correlation_id} />
      {problem.kind === 'forbidden' ? (
        <p className="mut">Master text edits need the Legal reviewer role (B-07: not yet granted in production).</p>
      ) : null}
      {problem.kind === 'not_found' ? <p className="mut">Not found — the code may have been mistyped.</p> : null}
      {problem.kind === 'not_implemented' ? (
        <p className="mut">
          Waiting on <code>POST /v1/clauses/actions</code>.
        </p>
      ) : null}
    </>
  );
}
